import type { Vec2 } from './vec';
import { v } from './vec';

const EPSILON = 1e-9;

// Wraps an angle in radians into (-PI, PI].
export function wrapAngle(a: number): number {
  const twoPi = 2 * Math.PI;
  let r = a % twoPi;
  if (r <= -Math.PI) r += twoPi;
  else if (r > Math.PI) r -= twoPi;
  return r;
}

// Closest point to p on the segment a-b, with the interpolation
// parameter t clamped to [0, 1].
export function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number } {
  const ab = v.sub(b, a);
  const abLenSq = v.dot(ab, ab);
  if (abLenSq === 0) return { point: a, t: 0 };
  const t = Math.max(0, Math.min(1, v.dot(v.sub(p, a), ab) / abLenSq));
  return { point: v.add(a, v.scale(ab, t)), t };
}

// Even-odd ray casting, with an explicit boundary check so a point exactly
// on an edge (or vertex) counts as inside.
export function pointInPolygon(p: Vec2, poly: Vec2[]): boolean {
  if (poly.length < 3) return false;

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (!a || !b) continue;
    const { point } = closestPointOnSegment(p, a, b);
    if (v.dist(point, p) < EPSILON) return true;
  }

  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const pi = poly[i];
    const pj = poly[j];
    if (!pi || !pj) continue;
    const intersects =
      pi.y > p.y !== pj.y > p.y &&
      p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function polygonCentroid(poly: Vec2[]): Vec2 {
  let sum: Vec2 = { x: 0, y: 0 };
  for (const p of poly) sum = v.add(sum, p);
  return v.scale(sum, 1 / poly.length);
}

// Minimum translation vector to move a circle (centre c, radius r) fully
// outside a convex polygon, or null if it does not overlap. For each edge
// we find the closest point on that edge to c and keep the closest overall.
// If c is outside the polygon and within r of that closest point, push c
// away from the polygon along (c - closest) by (r - distance). If c is
// inside the polygon, push it out through the nearest edge along
// (closest - c) by (r + distance), so the circle clears the edge entirely.
export function circlePolygonPush(c: Vec2, r: number, poly: Vec2[]): Vec2 | null {
  if (poly.length < 3) return null;

  let minDist = Infinity;
  let closest: Vec2 | null = null;
  let edgeA: Vec2 | null = null;
  let edgeB: Vec2 | null = null;

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    if (!a || !b) continue;
    const { point } = closestPointOnSegment(c, a, b);
    const d = v.dist(c, point);
    if (d < minDist) {
      minDist = d;
      closest = point;
      edgeA = a;
      edgeB = b;
    }
  }

  if (!closest || !edgeA || !edgeB) return null;

  const inside = pointInPolygon(c, poly);

  if (inside) {
    let dir = v.norm(v.sub(closest, c));
    if (dir.x === 0 && dir.y === 0) {
      // c sits exactly on the closest point; fall back to the edge normal
      // that points away from the polygon centroid.
      const edge = v.sub(edgeB, edgeA);
      const n1 = v.norm({ x: -edge.y, y: edge.x });
      const centroid = polygonCentroid(poly);
      const towardCentroid = v.dot(n1, v.sub(centroid, closest)) > 0;
      dir = towardCentroid ? v.scale(n1, -1) : n1;
    }
    return v.scale(dir, r + minDist);
  }

  if (minDist < r) {
    let dir = v.norm(v.sub(c, closest));
    if (dir.x === 0 && dir.y === 0) {
      const edge = v.sub(edgeB, edgeA);
      const n1 = v.norm({ x: -edge.y, y: edge.x });
      const centroid = polygonCentroid(poly);
      const towardCentroid = v.dot(n1, v.sub(centroid, closest)) > 0;
      dir = towardCentroid ? v.scale(n1, -1) : n1;
    }
    return v.scale(dir, r - minDist);
  }

  return null;
}
